import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Users, Fuel, Gauge, Check, Plane, ChevronLeft, ChevronRight, X, ZoomIn, Loader2, ShieldCheck, Star, CalendarCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useGallery } from "@/hooks/useGallery";
import { usePaymentReturn } from "@/hooks/usePaymentReturn";
import BookingPanel from "@/components/booking/BookingPanel";
import ConfirmationScreen from "@/components/booking/ConfirmationScreen";
import { StarRating } from "@/components/StarRating";
import { Seo } from "@/components/Seo";

export default function CarDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [config, setConfig] = useState(null);
  const [avail, setAvail] = useState({ available: true, blocked_ranges: [] });
  const [notFound, setNotFound] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get(`/cars/${id}`).then((res) => setCar(res.data)).catch(() => setNotFound(true));
    api.get(`/cars/${id}/availability`).then((res) => setAvail(res.data)).catch(() => {});
    api.get("/config").then((res) => setConfig(res.data)).catch(() => {});
  }, [id]);

  const gallery = car?.images?.length ? car.images : car?.image_url ? [car.image_url] : [];
  const { activeImg, setActiveImg, lightbox, setLightbox, prev, next } = useGallery(gallery);
  const verifying = usePaymentReturn(setConfirmed);

  if (verifying)
    return (
      <div data-testid="payment-verifying" className="min-h-screen flex flex-col items-center justify-center bg-white text-slate-600 gap-4 px-5">
        <Loader2 size={40} className="text-blue-600 animate-spin" />
        <p className="font-heading text-xl text-slate-900 font-semibold">{t("carDetail.confirmingPayment")}</p>
        <p className="text-slate-500 text-sm">{t("carDetail.dontClose")}</p>
      </div>
    );

  if (confirmed) return <ConfirmationScreen confirmed={confirmed} />;

  if (notFound)
    return (
      <div data-testid="car-not-found" className="min-h-screen flex flex-col items-center justify-center bg-white text-center px-6">
        <p className="text-slate-600">{t("carDetail.notFound")}</p>
        <Link to="/fleet" className="mt-6 font-semibold text-white bg-blue-600 px-6 py-3 rounded-full hover:bg-blue-700">{t("carDetail.backToFleet")}</Link>
      </div>
    );

  if (!car) return <div className="min-h-screen flex items-center justify-center bg-white text-slate-400">{t("common.loading")}</div>;

  const specs = [
    { icon: Users, label: t("carDetail.seats"), value: car.seats },
    { icon: Gauge, label: t("carDetail.transmission"), value: car.transmission },
    { icon: Fuel, label: t("carDetail.fuel"), value: car.fuel },
    { icon: Check, label: t("carDetail.category"), value: car.category },
  ];
  const mainImg = gallery[Math.min(activeImg, gallery.length - 1)];
  const fromDaily = Math.round(car.rates?.daily_start || car.weekly_rate / 7);
  const rt = car.rates || {};
  const rtTiers = rt.tiers || {};

  return (
    <div data-testid="car-detail-page" className="bg-white min-h-screen pt-24 pb-32 xl:pb-24 px-5 sm:px-8">
      <Seo
        title={`${car.name} Rental — Boston & Logan Airport | Belo Rentals`}
        description={`Rent the ${car.name} from $${fromDaily}/day with Logan Airport pickup & drop-off in Boston. ${car.seats} seats, ${car.transmission}. Book online with Belo Rentals.`}
        path={`/cars/${car.id}`}
        image={mainImg}
        type="product"
        keywords={`${car.name} rental Boston, ${car.make} rental Logan Airport, car rental Boston, airport car rental`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: car.name,
          image: gallery,
          description: car.description || `${car.name} rental in Boston with Logan Airport pickup & drop-off.`,
          brand: { "@type": "Brand", name: car.make },
          ...(car.stock_number ? { sku: car.stock_number } : {}),
          offers: {
            "@type": "Offer",
            price: String(fromDaily),
            priceCurrency: "USD",
            availability: avail.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: `https://belorentals.com/cars/${car.id}`,
          },
        }}
      />
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate(-1)} data-testid="back-button" className="flex items-center gap-2 text-slate-500 hover:text-slate-900 font-medium transition-colors mb-6">
          <ArrowLeft size={16} /> {t("common.back")}
        </button>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* LEFT — gallery + (bottom) details */}
          {/* A: title + gallery */}
          <div className="xl:col-span-7 xl:col-start-1 xl:row-start-1 flex flex-col gap-8">
            <div>
              <p className="label-caps text-blue-600">{car.make}</p>
              <h1 className="font-heading text-3xl lg:text-4xl text-slate-900 mt-1 tracking-tight font-bold">{car.name}</h1>
              <p className="text-slate-500 mt-2"><span className="text-slate-900 font-semibold">${fromDaily}</span> {t("carDetail.perDayFrom")} · ${car.deposit} {t("carDetail.refundableDeposit")}</p>
              {car.host_id && car.host_name && (
                <Link to={`/u/${car.host_id}`} data-testid="car-host-link" className="inline-flex items-center gap-2 mt-3 group">
                  <span className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 grid place-items-center text-xs font-bold">{car.host_name.charAt(0).toUpperCase()}</span>
                  <span className="text-sm text-slate-600">{t("publicProfile.hostedBy")} <span className="font-semibold text-slate-900 group-hover:text-blue-600 group-hover:underline transition-colors">{car.host_name}</span></span>
                </Link>
              )}
            </div>

            <div>
              <button onClick={() => setLightbox(true)} data-testid="gallery-open" className="group relative aspect-[4/3] w-full overflow-hidden rounded-3xl bg-slate-100 shadow-card cursor-zoom-in block">
                <img src={mainImg} alt={car.name} data-testid="gallery-main" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute bottom-4 right-4 flex items-center gap-1.5 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur"><ZoomIn size={14} /> {t("carDetail.viewPhotos")}</span>
                {gallery.length > 1 && (
                  <>
                    <span onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="main-prev" className="absolute left-3 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 rounded-full bg-white/80 text-slate-800 hover:bg-white shadow transition-colors"><ChevronLeft size={20} /></span>
                    <span onClick={(e) => { e.stopPropagation(); next(); }} data-testid="main-next" className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 rounded-full bg-white/80 text-slate-800 hover:bg-white shadow transition-colors"><ChevronRight size={20} /></span>
                  </>
                )}
              </button>
              {gallery.length > 1 && (
                <div className="grid grid-cols-5 gap-3 mt-3">
                  {gallery.map((img, i) => (
                    <button key={img} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`} className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${i === activeImg ? "border-blue-600" : "border-transparent opacity-70 hover:opacity-100"}`}>
                      <img src={img} alt={`${car.name} ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* B: booking panel — right after the gallery on mobile, sticky on the right on desktop */}
          <div className="xl:col-span-5 xl:col-start-8 xl:row-start-1 xl:row-span-2">
            <div className="xl:sticky xl:top-24">
              <BookingPanel car={car} config={config} avail={avail} initial={{ pickup_date: sp.get("start") || undefined, return_date: sp.get("end") || undefined, pickup_location: sp.get("loc") || undefined }} />
            </div>
          </div>

          {/* C: rates, specs, description & features pushed below */}
          <div className="xl:col-span-7 xl:col-start-1 xl:row-start-2 flex flex-col gap-10">
            {/* Rate table */}
            {rtTiers[7] && (
              <div data-testid="rate-table" className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 bg-slate-900">
                  <div>
                    <p className="font-heading font-semibold text-white">{t("carDetail.rentalRates")}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{rt.category || car.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-300 text-sm">{t("carDetail.fromPerDay")} <span className="text-white font-semibold">${fromDaily}</span>/day</p>
                    <p className="text-slate-500 text-[10px]">{t("carCard.beforeTaxes")}</p>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {[2, 3, 4, 5, 6].map((d) => (
                    <div key={d} className="flex justify-between items-center px-6 py-3 text-sm"><span className="text-slate-600">{d} {t("carDetail.days")}</span><span className="text-slate-900 font-semibold">${rtTiers[d]}</span></div>
                  ))}
                  <div className="flex justify-between items-center px-6 py-3.5 text-sm bg-blue-50"><span className="text-slate-800 font-semibold">{t("carDetail.week")} <span className="text-slate-400 font-normal">{t("carDetail.sevenDays")}</span> <span className="ml-1.5 text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full align-middle">{t("carDetail.bestValue")}</span></span><span className="text-blue-700 font-bold text-base">${rtTiers[7]}</span></div>
                  <div className="flex justify-between items-center px-6 py-3 text-sm"><span className="text-slate-600">{t("carDetail.extraDay")}</span><span className="text-slate-900 font-semibold">+${rt.extra_day || 50}</span></div>
                </div>
                <div className="px-6 py-4 bg-slate-50 space-y-2.5">
                  <p className="flex items-start gap-2 text-slate-600 text-xs leading-relaxed"><Gauge size={15} className="text-blue-600 mt-0.5 shrink-0" /><span><span className="font-semibold text-slate-800">{t("carDetail.mileageLabel")}</span> {t("carDetail.mileageDetail", { perDay: config?.miles_per_day, perWeek: Number(config?.miles_per_week || 0).toLocaleString(), perMonth: Number(config?.miles_per_month || 0).toLocaleString(), overage: config?.miles_overage_rate, unlimited: config?.unlimited_miles_per_day })}</span></p>
                  <p className="flex items-start gap-2 text-slate-600 text-xs leading-relaxed"><ShieldCheck size={15} className="text-blue-600 mt-0.5 shrink-0" /><span>{t("carDetail.depositDetail", { deposit: car.deposit })}</span></p>
                  <p data-testid="rates-disclaimer" className="text-slate-400 text-[11px] leading-relaxed pt-1">{t("carDetail.ratesDisclaimer")}</p>
                </div>
                <div className="px-6 py-4 border-t border-slate-100">
                  <Link to="/fleet" data-testid="browse-vehicles-cta" className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white py-3 rounded-full font-semibold text-sm hover:bg-blue-700 transition-colors">{t("carDetail.browseVehicles")}</Link>
                </div>
              </div>
            )}

            {/* Host profile & reputation */}
            {car.host_name && (
              <div data-testid="host-profile-block" className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-6 flex items-start gap-5">
                  {car.host_photo ? (
                    <img src={car.host_photo} alt={car.host_name} data-testid="host-profile-photo" className="w-20 h-20 rounded-full object-cover border border-slate-200 shrink-0" />
                  ) : (
                    <span data-testid="host-profile-initials" className="w-20 h-20 rounded-full bg-blue-50 border border-blue-100 grid place-items-center font-heading text-3xl font-bold text-blue-700 shrink-0">
                      {car.host_name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="label-caps text-blue-600">Hosted by</p>
                    <Link to={car.host_profile_url || "/fleet"} data-testid="host-profile-name" className="font-heading text-2xl font-bold text-slate-900 hover:text-blue-600 transition-colors block leading-tight">{car.host_name}</Link>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2 text-sm">
                      {car.host_rating?.count > 0 ? (
                        <span data-testid="host-profile-rating" className="flex items-center gap-1.5">
                          <StarRating value={car.host_rating.avg} size={15} />
                          <span className="font-semibold text-slate-900">{car.host_rating.avg.toFixed(1)}</span>
                          <span className="text-slate-400">({car.host_rating.count})</span>
                        </span>
                      ) : (
                        <span data-testid="host-profile-rating" className="flex items-center gap-1.5 text-slate-400"><Star size={15} /> No reviews yet</span>
                      )}
                      <span data-testid="host-profile-trips" className="flex items-center gap-1.5 text-slate-600"><CalendarCheck size={15} className="text-blue-600" /> {car.trips_completed || 0} {car.trips_completed === 1 ? "trip" : "trips"} completed</span>
                    </div>
                    <Link to={car.host_profile_url || "/fleet"} data-testid="host-profile-link" className="inline-block mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700">
                      {car.is_belo_host ? "View full fleet →" : "View host's inventory & reviews →"}
                    </Link>
                  </div>
                </div>
                {car.host_reviews?.length > 0 && (
                  <div className="border-t border-slate-100 divide-y divide-slate-100">
                    {car.host_reviews.map((r) => (
                      <div key={r.id} data-testid={`host-review-${r.id}`} className="px-6 py-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-semibold text-slate-900 text-sm">{r.author_name}</p>
                          <StarRating value={r.rating} size={13} />
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">{r.car_name} · {new Date(r.created_at).toLocaleDateString()}</p>
                        {r.comment && <p className="text-slate-600 text-sm mt-2 leading-relaxed">{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Specs */}
            <div>
              <h2 className="font-heading text-2xl text-slate-900 font-bold tracking-tight">{t("carDetail.vehicleDetails")}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
                {specs.map((s) => (
                  <div key={s.label} className="bg-slate-50 rounded-2xl p-5">
                    <s.icon size={20} className="text-blue-600" />
                    <p className="label-caps text-slate-400 mt-3">{s.label}</p>
                    <p className="text-slate-900 font-medium mt-1">{s.value}</p>
                  </div>
                ))}
              </div>
              {(car.stock_number || car.vin) && (
                <div data-testid="car-identification" className="flex flex-wrap items-center gap-x-8 gap-y-2 mt-5 px-1 text-sm">
                  {car.stock_number && <span className="text-slate-500">{t("carDetail.stockHash")} <span className="font-semibold text-slate-900">{car.stock_number}</span></span>}
                  {car.vin && <span className="text-slate-500">VIN <span className="font-mono font-semibold text-slate-900">{car.vin}</span></span>}
                </div>
              )}
            </div>

            {/* Description */}
            {car.description && (
              <div>
                <h2 className="font-heading text-2xl text-slate-900 font-bold tracking-tight">{t("carDetail.aboutCar")}</h2>
                <p className="text-slate-600 leading-relaxed mt-4">{car.description}</p>
              </div>
            )}

            {/* Features */}
            {car.features?.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl text-slate-900 font-bold tracking-tight">{t("carDetail.features")}</h2>
                <div className="flex flex-wrap gap-2 mt-4">
                  {car.features.map((f) => (
                    <span key={f} className="text-sm text-slate-700 bg-slate-100 px-3 py-1.5 rounded-full">{f}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-blue-700 bg-blue-50 px-4 py-3 rounded-xl text-sm font-medium w-max">
              <Plane size={16} /> {t("carDetail.airportNote")}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div data-testid="lightbox" className="fixed inset-0 z-[60] bg-black/95 flex flex-col" onClick={() => setLightbox(false)}>
          <div className="flex items-center justify-between px-5 sm:px-8 py-4 text-white">
            <span className="font-heading font-semibold">{car.name}</span>
            <div className="flex items-center gap-5">
              <span data-testid="lightbox-counter" className="text-sm text-white/70">{activeImg + 1} / {gallery.length}</span>
              <button onClick={() => setLightbox(false)} data-testid="lightbox-close" className="grid place-items-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors"><X size={22} /></button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 pb-4 relative" onClick={(e) => e.stopPropagation()}>
            {gallery.length > 1 && <button onClick={prev} data-testid="lightbox-prev" className="absolute left-3 sm:left-8 grid place-items-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><ChevronLeft size={26} /></button>}
            <img src={gallery[activeImg]} alt={`${car.name} ${activeImg + 1}`} data-testid="lightbox-image" className="max-h-[78vh] max-w-[92vw] object-contain rounded-xl" />
            {gallery.length > 1 && <button onClick={next} data-testid="lightbox-next" className="absolute right-3 sm:right-8 grid place-items-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><ChevronRight size={26} /></button>}
          </div>
          {gallery.length > 1 && (
            <div className="flex justify-center gap-2 pb-6 px-4 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
              {gallery.map((img, i) => (
                <button key={img} onClick={() => setActiveImg(i)} data-testid={`lightbox-thumb-${i}`} className={`h-14 w-20 shrink-0 rounded-lg overflow-hidden border-2 transition-all ${i === activeImg ? "border-white" : "border-transparent opacity-50 hover:opacity-90"}`}>
                  <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
